<template>
  <div class="global-leaderboard" v-if="!loading">
    <h1>Global Leaderboard</h1>
    <p class="description">
      The global leaderboard shows the top 100 rounds based on Tjing Rating. You
      can also see the hottest rounds based on the par and only show the best
      round for each player.
    </p>
    <div class="content-header">
      <div class="desktop-nav">
        <CustomDropDown
          position="left"
          :items="sortItems"
          @changeSort="
            sortBy = $event
            loadData()
          "
        >
          <div class="selector-wrapper">
            <p>
              <b>{{ selectedSortName }}</b>
            </p>
            <ChevronFat direction="down" />
          </div>
        </CustomDropDown>
        <div class="unique-toggle-wrapper">
          <div class="toggle-label">
            <span>Only best rounds</span>
            <DefaultToggle
              :value="uniqueUsers"
              @input="
                uniqueUsers = $event
                loadData()
              "
            />
          </div>
        </div>
      </div>
    </div>

    <SmartStickyHeader>
      <div class="mobile-nav">
        <select
          @change="
            sortBy = $event.target.value
            loadData()
          "
          v-model="sortBy"
          class="sort-selector"
        >
          <option value="RATING">Rating</option>
          <option value="PAR">Par</option>
        </select>
        <div class="mobile-toggle">
          <div class="toggle-label">
            <span>Best rounds only</span>
            <DefaultToggle
              :value="uniqueUsers"
              @input="
                uniqueUsers = $event
                loadData()
              "
            />
          </div>
        </div>
      </div>
    </SmartStickyHeader>

    <div class="standings-table">
      <div class="table-header">
        <div class="position">
          <p>Rank</p>
        </div>
        <div class="player">
          <p>Player</p>
        </div>
        <div class="score">
          <p>Score</p>
        </div>
        <div class="par">
          <p>Par</p>
        </div>
        <div class="rating">
          <p>Rating</p>
        </div>
        <div class="date">
          <p>Date</p>
        </div>
        <div class="course">
          <p>Course</p>
        </div>
        <div class="event">
          <p>Event</p>
        </div>
      </div>
      <div class="table-body">
        <div class="player-row" v-for="player in leaderboardData">
          <div class="position">
            <p>{{ player.position }}</p>
          </div>
          <div class="player">
            <ProfilePopoverCard :userId="player.userId">
              <div class="player-wrapper">
                <UserProfileAvatar
                  size="S"
                  desktopSize="D-M"
                  :profileImageUrl="player.user.profile.profileImageUrl"
                  :initials="
                    player.user.firstName.charAt(0) +
                    player.user.lastName.charAt(0)
                  "
                />
                <p>{{ player.user.firstName }} {{ player.user.lastName }}</p>
              </div>
            </ProfilePopoverCard>
          </div>
          <div class="score">
            <p>{{ player.totalThrows }}</p>
          </div>
          <div class="par">
            <p>{{ convertPar(player.totalPar) }}</p>
          </div>
          <div class="rating">
            <TjingTextWrapTooltip
              :unique="'rating-' + player.userId + '-' + player.eventId"
              class="tooltip-container"
            >
              <template v-slot:tooltip>
                <p><b>Date:</b> {{ formatDate(player.date) }}</p>
                <p>
                  <b>Course:</b> {{ player.courseName }},
                  {{ player.layoutName }}
                </p>
                <router-link
                  :to="{
                    name: 'public-event-division-results',
                    params: { eventId: player.eventId },
                  }"
                  class="event-link"
                >
                  View event
                </router-link>
              </template>
              <template v-slot:content>
                <span class="rating-value">{{ player.rating }}</span>
              </template>
            </TjingTextWrapTooltip>
          </div>
          <div class="date">
            <p>{{ formatDate(player.date) }}</p>
          </div>
          <div class="course" v-if="player.course.type === 'PUBLIC'">
            <p>
              <router-link
                :to="{
                  name: 'course-overview',
                  params: { courseId: player.courseId },
                }"
                class="course-link"
              >
                {{ player.courseName }}
              </router-link>
              ,
              <router-link
                :to="{
                  name: 'course-layout',
                  params: {
                    courseId: player.courseId,
                    layoutId: player.layoutId,
                  },
                }"
                class="layout-link"
              >
                {{ player.layoutName }}
              </router-link>
            </p>
          </div>
          <div class="course" v-else>
            <p>{{ player.courseName }}, {{ player.layoutName }}</p>
          </div>
          <div class="event">
            <router-link
              :to="{
                name: 'public-event-division-results',
                params: { eventId: player.eventId },
              }"
              class="event-link"
            >
              <EventIcon />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProfilePopoverCard from '@/components/ProfilePopoverCard'
import UserProfileAvatar from '@/components/UIElements/UserProfileAvatar'
import CustomDropDown from '@/components/CustomDropDown.vue'
import ChevronFat from '@/assets/icons/ChevronFat.vue'
import DefaultToggle from '@/components/UIElements/DefaultToggle.vue'
import EventIcon from '@/assets/icons/CopyLink.vue'
import SmartStickyHeader from '@/components/SmartStickyHeader.vue'
import TjingTextWrapTooltip from '@/components/UIElements/TjingTextWrapTooltip'

export default {
  name: 'GlobalLeaderboard',
  components: {
    ProfilePopoverCard,
    UserProfileAvatar,
    CustomDropDown,
    ChevronFat,
    DefaultToggle,
    EventIcon,
    SmartStickyHeader,
    TjingTextWrapTooltip,
  },
  data() {
    return {
      loading: true,
      leaderboardData: [],
      sortBy: 'RATING',
      uniqueUsers: false,
    }
  },
  computed: {
    selectedSortName() {
      const sortMap = {
        RATING: 'Rating',
        PAR: 'Total Par',
      }
      return sortMap[this.sortBy] || 'Rating'
    },
    sortItems() {
      return [
        {
          title: 'Rating',
          action: 'changeSort',
          value: 'RATING',
          selected: this.sortBy === 'RATING',
        },
        {
          title: 'Par',
          action: 'changeSort',
          value: 'PAR',
          selected: this.sortBy === 'PAR',
        },
      ]
    },
    direction() {
      return this.sortBy === 'PAR' ? 'ASC' : 'DESC'
    },
  },
  methods: {
    convertPar(par) {
      if (par == 0) {
        return 'E'
      } else if (par > 0) {
        return '+' + par
      } else {
        return par
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    async loadData() {
      try {
        const result = await this.$axios({
          data: {
            query: `
            query CompetitiveRoundsLeaderboard {
                competitiveRoundsLeaderboard(sortBy: ${this.sortBy}, direction: ${this.direction}, uniqueUsers: ${this.uniqueUsers}, first: 100) {
                    poolId
                    userId
                    courseId
                    courseName
                    layoutId
                    layoutName
                    layoutPar
                    date
                    totalThrows
                    totalPar
                    rating
                    eventId
                    course{
                        type
                    }
                    user {
                        id
                        firstName
                        lastName
                        profile{
                            profileImageUrl
                        }
                    }
                    position
                }
            }
            `,
          },
        })

        this.leaderboardData = result.data.data.competitiveRoundsLeaderboard
        this.loading = false
      } catch (error) {
        console.error('Error loading leaderboard data:', error)
        this.loading = false
      }
    },
  },
  mounted() {
    this.loadData()
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/Typography';

h1 {
  margin-top: 24px;
  font-size: 2rem;
}
.description {
  margin-top: 12px;
  font-size: 1rem;
  color: $strom;
  line-height: 1.5rem;
  margin-bottom: 24px;
}

.global-leaderboard {
  padding: 0 12px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 24px 0;
  gap: 16px;
}

.mobile-nav {
  margin: 0 0 24px 0;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: stretch;

  .sort-selector,
  .direction-selector {
    margin: 0;
    height: 40px;
    background-position-x: calc(100% - 12px);
    border: 1px solid $midnight;
    border-radius: 6px;
    width: auto;
    padding-right: 40px;
    flex-grow: 1;
    flex-basis: 0;
  }

  .sort-selector {
    flex-basis: 65%;
  }

  .direction-selector {
    flex-shrink: 2;
    max-width: 100%;
  }

  .mobile-toggle {
    width: 100%;

    .toggle-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;

      span {
        font-size: 14px;
        color: $midnight;
      }
    }
  }
}

.desktop-nav {
  display: none;
  align-items: center;
  gap: 16px;

  .selector-wrapper {
    display: flex;
    align-items: center;
    border: 1px solid $midnight;
    padding: 14px 16px;
    border-radius: 6px;

    p {
      font-size: 16px;
      margin: 0;
    }

    svg {
      margin-left: 12px;
      width: 16px;
      height: 12px;
    }
  }

  .unique-toggle-wrapper {
    margin-left: 16px;

    .toggle-label {
      display: flex;
      align-items: center;
      gap: 12px;

      span {
        font-size: 14px;
        color: $midnight;
        white-space: nowrap;
      }
    }
  }
}

.standings-table {
  margin-top: 12px;

  .table-header,
  .player-row {
    display: flex;
    justify-items: center;
    align-items: center;
  }

  .table-header {
    height: 40px;
    background: $mist;
    border-radius: 8px;
    align-items: center;
    margin-bottom: 12px;

    p {
      @include Gilroy-SemiBold;
      font-size: 1rem;
      color: $strom;
    }
  }

  .player-row {
    height: 48px;
    border-bottom: 1px solid $fog;
    align-items: center;

    p {
      font-size: 1rem;
    }

    .player {
      cursor: pointer;

      > div {
        max-width: 100%;
      }

      .player-wrapper {
        cursor: pointer;
        display: flex;
        align-items: center;

        p {
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-left: 8px;
          overflow: hidden;
        }
      }
    }

    .course-link,
    .layout-link {
      color: $ocean;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    .event-link {
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 20px;
        height: 20px;
        color: $ocean;
      }
    }
  }

  .position {
    width: 60px;
    min-width: 60px;
    display: flex;
    align-items: center;
    justify-content: center;

    p {
      text-align: center;
    }
  }

  .player {
    display: flex;
    align-items: center;
    justify-self: start;
    overflow: hidden;
    flex: 1;
  }

  .course,
  .event,
  .score,
  .par,
  .date {
    display: none;
    align-items: center;
  }

  .rating {
    padding-right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .position {
    align-self: center;
    justify-self: center;
  }
}

.tooltip-container {
  .event-link {
    color: $ocean;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  p {
    margin-bottom: 6px;
    color: white !important;
  }

  .rating-value {
    display: block;
    width: 100%;
    text-align: center;
  }
}

@media (min-width: 544px) {
  .standings-table {
    .table-header,
    .player-row {
      justify-content: flex-start;

      .position {
        min-width: 50px;
      }

      .player {
        flex: unset;
        width: 100%;
        min-width: 175px;
      }

      .event {
        justify-content: center;
      }

      .event,
      .par,
      .rating,
      .date {
        display: flex;
        min-width: 60px;
      }
      .date {
        min-width: 125px;
      }
    }
  }
}

@media (min-width: 768px) {
  .standings-table {
    .table-header,
    .player-row {
      .course {
        width: clamp(200px, 25%, 300px);
        min-width: 200px;
      }

      .event {
        width: 60px;
        min-width: 60px;
        justify-content: center;
      }

      .score,
      .par,
      .rating {
        display: flex;
        width: clamp(60px, 8%, 80px);
        justify-content: center;
      }
      .course {
        display: flex;
        width: clamp(175px, 30%, 300px);
        min-width: 175px;
      }
    }
  }
}

@media (min-width: 1200px) {
  h1 {
    font-size: 2.5rem;
  }
  .description {
    max-width: 50%;
    font-size: 1rem;
    line-height: 1.5rem;
    margin-bottom: 64px;
  }
  .mobile-nav {
    display: none;
  }

  .desktop-nav {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }

  .standings-table {
    .table-header,
    .player-row {
      padding-right: 16px;

      .position {
        min-width: 60px;
      }

      .player {
        flex: 1;
        min-width: 200px;
        max-width: none;

        .player-wrapper {
          p {
            margin-left: 12px;
          }
        }
      }

      .course {
        width: clamp(250px, 30%, 400px);
        min-width: 250px;
        padding-right: 24px;
      }

      .event {
        width: 60px;
        min-width: 60px;
        justify-content: center;
      }

      .score,
      .par,
      .rating {
        min-width: 80px;
        justify-content: center;
        padding-right: 0;

        p {
          text-align: center;
        }
      }
    }

    .table-header {
      height: 48px;
      margin-bottom: 16px;
    }

    .player-row {
      height: 56px;
    }
  }
}
</style>
